// Your routes — and the navigation binding's, which is the part worth knowing.
//
// THIS FILE IS REPLACED when the navigation shell is re-applied. `bottom-tabs`,
// `stack` and `sidebar` produce different routers, so the binding owns it and
// `appfactory spec apply` rewrites it with `overwrite: true`. Commit before
// running one, because everything below has been edited by hand.
//
// TWO EDITS AGAINST THE GENERATED VERSION, both worth keeping:
//
//   1. Paths are NORMALISED under /tabs/. The product plan gives Home the route
//      `/`, which is right in the plan's vocabulary and fatal in this shell:
//      the generated router mapped it to a child of `/tabs/` whose path was the
//      absolute string `/`, and then redirected `/` to it. Vue Router resolved
//      that to itself and the app never finished its first navigation.
//
//   2. The tabs carry NO ICONS. This app's design has one rule — the only
//      saturated pixels on any screen belong to the train lines — and the
//      corollary is that its chrome is text. Ionicons are a set of glyphs with
//      no train line in them, so the bar is set in Martian Mono at 10px and
//      styled in src/styles/app.css. `TAB_ICONS` is gone with them.
//
//   3. The WELCOME is a top-level route and it can claim the first navigation.
//      It sits outside `/tabs/` because a first run has no destinations to
//      switch between, and a tab bar under a four-step question invites people
//      to leave through it. See the guard at the foot of this file.

import { createRouter, createMemoryHistory } from '@ionic/vue-router';

import TabsShell from './pages/TabsShell.vue';
import { onboardingNeeded } from './app-state.js';
import { PRODUCT_ROUTES } from './product-routes.js';
import { TRIP_PATHS, modeRedirects, tripRedirect } from './trip-redirect.js';
import { trainRunRedirect } from './run-redirect.js';

/** `/` → `/tabs/home`, `/disruption/:id` → `/tabs/disruption/:id`. */
const tabPath = (route) => `/tabs/${route.path === '/' ? 'home' : route.path.replace(/^\//, '')}`;

const ROUTES = PRODUCT_ROUTES.map((r) => ({ ...r, path: tabPath(r) }));

/** Where the app opens. main.js pushes this. */
// Opens on the departures board: the leftmost tab is the one the app starts
// on, and "what is the next train" is the question asked most often.
export const HOME = ROUTES.find((r) => r.id === 'departures')?.path ?? ROUTES[0].path;

/** The welcome, outside the tab shell. Settings can send you back to it. */
export const ONBOARDING = '/onboarding';

// The tabs, in bar order.
//
// A tab bar is for DESTINATIONS. Choose lines, Disruption, Line, Journey and
// Options are all pushed from somewhere — a button on Home, a row on the
// ledger, an annotated row on the board — so none of them is here. That leaves
// the four places you can actually go: when the next train is (and, with a
// destination set, how to get there), where the trains are on the network,
// what is happening to your lines, and what was sent before. SIGNAL BOX took
// the map's slot on 25 September 2026: the game is where people go while a
// train is late, and the map is still one row away under Lines.
//
// THERE WAS A TRIP TAB, second in the bar. The board with a destination set
// plans the change, honours what to avoid and opens the same journey page, so
// a second screen asking the same question was retired and the map, which had
// been three taps deep under Lines, took its place. `/tabs/directions` still
// resolves — see src/trip-redirect.js — because links and habits outlive tabs.
//
// FIVE IS THE CEILING. `src/pages/SHELL.md` says a sixth destination is the
// signal to re-resolve this app with `navigation: "sidebar"` rather than to
// add a More tab — and the design has its own reason to stop here: the bar is
// text, not icons, and a sixth label does not fit across 320px in Martian Mono
// without shrinking below the point it can be read at a glance on a platform.
const TAB_IDS = ['departures', 'home', 'signal-box', 'alerts'];

const TAB_LABELS = {
  home: 'Lines',
  departures: 'Trains',
  map: 'Map',
  // GAME, not SIGNAL BOX: ten letters of Martian Mono truncate at 375px.
  'signal-box': 'Game',
  alerts: 'Alerts',
  settings: 'Settings',
};

export const TABS = TAB_IDS
  .map((id) => ROUTES.find((r) => r.id === id))
  .filter(Boolean)
  .map((r) => ({ name: r.id, label: TAB_LABELS[r.id] ?? r.label, path: r.path }));

const router = createRouter({
  history: createMemoryHistory(),
  routes: [
    { path: '/', redirect: HOME },
    { path: ONBOARDING, name: 'onboarding', component: () => import('./pages/OnboardingPage.vue') },
    {
      // The tab shell owns its own outlet, so each tab keeps its own
      // navigation stack. Pushing /tabs/disruption/3 leaves the tab bar in
      // place and the back button working.
      path: '/tabs/',
      component: TabsShell,
      children: [
        { path: '', redirect: HOME },
        ...TRIP_PATHS.map((p) => ({ path: p.replace(/^\/tabs\//, ''), redirect: tripRedirect })),
        // The retired Plan and Trip screens of a mode with stops of its own.
        ...modeRedirects(),
        // `props: true` on every route, so the Disruption screen gets its `id`
        // as a prop as well as through useRoute().
        ...ROUTES.map((r) => ({
          path: r.path.replace(/^\/tabs\//, ''),
          name: r.id,
          component: r.component,
          props: true,
          meta: r.meta,
          // A tram's run opened on the train path (an older build's lock-screen
          // link) goes to its own screen. See src/run-redirect.js.
          ...(r.id === 'train' ? { beforeEnter: trainRunRedirect } : {}),
        })),
      ],
    },
  ],
});

// The first navigation, and ONLY the first.
//
// main.js pushes `HOME` and mounts on `isReady()`, so the one place a welcome
// can be inserted without a flash of the ledger is inside that initial
// navigation — the redirect happens before it finalises, so `isReady()` still
// resolves exactly once, at the right screen.
//
// It unregisters itself immediately. A standing guard would re-ask the
// question on every tab change for the rest of the session, and would fight
// the person who pressed "Not now" by sending them back the moment they
// navigated. `onboardingNeeded()` is where the rule lives; this is only the
// hook it hangs from.
const stopFirstNavigation = router.beforeEach(async (to) => {
  stopFirstNavigation();
  if (to.path === ONBOARDING) return true;
  return (await onboardingNeeded()) ? ONBOARDING : true;
});

export default router;
